"use client";

import { useMemo } from "react";
import dayjs from "dayjs";

import { useGetSavingsSummaryQuery } from "@/app/redux/features/api/membersHome";
import ActiveMemberBadge from "@/app/(custom-header)/botb-pass-members-home/components/ActiveMemberBadge";
import SavingsCard from "./SavingsCard";
import ChevronsRightIcon from "@/app/shared-components/icons/ChevronsRightIcon";
import ClockIcon from "@/app/shared-components/icons/ClockIcon";
import { useSubscriptionContext } from "../subscriptionContext";

// Flip to `true` to preview the "member has savings" state without real API data.
const USE_MOCKS = false;
const MOCK_SAVINGS = {
  LifetimeTotal: 487.65,
  CurrentPeriodTotal: 45.3,
  CurrentPeriodStart: "14 Feb 26",
  CurrentPeriodEnd: "13 Mar 26",
};

function formatMemberSince(unixSeconds?: number): string {
  if (!unixSeconds) return "";
  const date = new Date(unixSeconds * 1000);
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${day} ${month} ${year}`;
}

export default function MemberHero() {
  const {
    subscription,
    subsWillBeCancelled,
    cancellationSubscriptionText,
    setIsBenefitsModalOpen,
    setIsRenewSubscriptionModalOpen,
  } = useSubscriptionContext();

  const { data: apiSavings } = useGetSavingsSummaryQuery();
  const savings = USE_MOCKS ? { ...apiSavings, ...MOCK_SAVINGS } : apiSavings;

  const remainingTime = useMemo(() => {
    const diff = dayjs(subscription.current_period_end * 1000).diff(dayjs(), "milliseconds");
    if (diff <= 0) return "0 hours";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return days === 1 ? `${days} day` : `${days} days`;
    return hours === 1 ? `${hours} hour` : `${hours} hours`;
  }, [subscription]);

  const percentageForRemaining = useMemo(() => {
    const total = subscription.current_period_end - subscription.current_period_start;
    const remaining = subscription.current_period_end - dayjs().unix();
    return Math.min(Math.abs(100 - (remaining / total) * 100), 100);
  }, [subscription]);

  const memberSince = useMemo(() => formatMemberSince(savings?.MemberSince), [savings?.MemberSince]);
  const displayName = savings?.FirstName && savings?.Surname ? `${savings.FirstName} ${savings.Surname}` : "";
  const planLabel = subscription?.name ? `${subscription.name} Plan` : "";
  const currentPeriod =
    savings?.CurrentPeriodStart && savings?.CurrentPeriodEnd
      ? `${savings.CurrentPeriodStart} - ${savings.CurrentPeriodEnd}`
      : "";

  const isCancelled = subsWillBeCancelled;
  const remainingLabel = remainingTime;
  const progressPercentage = percentageForRemaining;
  const cancellationText = cancellationSubscriptionText;

  const showRenew = isCancelled && progressPercentage < 100;

  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,437px)_minmax(0,1fr)] lg:items-start gap-y-6 lg:gap-x-10 px-4 lg:px-8 pt-8 lg:pt-16 pb-6">
        {/* Member info — centered on mobile, left-aligned on desktop */}
        <div className="order-1 lg:col-start-1 lg:row-start-1 flex flex-col gap-3 lg:gap-4 items-center lg:items-start w-full">
          {isCancelled ? (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-4 border border-red-3">
              <ClockIcon className="size-3.5 shrink-0 text-red-2" />
              <span className="text-red-2 text-xs lg:text-sm font-medium font-jost uppercase leading-none">
                Cancelled
              </span>
            </div>
          ) : (
            <ActiveMemberBadge />
          )}

          <div className="normal-case text-center lg:text-left !mb-0 text-dark-1 text-[22px] lg:text-[32px] font-bold font-jost leading-7 lg:leading-10">
            {displayName}
          </div>

          <div className="flex items-center justify-center lg:justify-start gap-2 flex-wrap">
            {planLabel && (
              <span className="text-gray-1 text-sm lg:text-base font-medium font-jost leading-5 lg:leading-5.5">
                {planLabel}
              </span>
            )}
            {planLabel && memberSince && <div className="size-1.5 rounded-full bg-gray-1 shrink-0" />}
            {memberSince && (
              <span className="text-gray-1 text-sm lg:text-base font-medium font-jost leading-5 lg:leading-5.5">
                Member since {memberSince}
              </span>
            )}
          </div>
        </div>

        {/* Savings card — sits between member info and progress on mobile, right column on desktop */}
        <div className="order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 relative w-full lg:max-w-[435px] lg:justify-self-end">
          <img
            src="/assets/images/botb-pass-members/coins.png"
            alt=""
            className="absolute -left-8 lg:-left-12 bottom-4 lg:bottom-6 w-[56px] lg:w-[96px] pointer-events-none select-none z-0 object-contain"
          />
          <div className="relative z-10">
            <SavingsCard
              totalSavings={savings?.LifetimeTotal ?? 0}
              currentSavings={savings?.CurrentPeriodTotal ?? 0}
              currentPeriod={currentPeriod}
            />
          </div>
        </div>

        {/* Progress + actions */}
        <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2 flex flex-col gap-6 w-full">
          {isCancelled && (
            <div className="flex flex-col gap-2 w-full">
              {progressPercentage < 100 && (
                <div className="text-secondary font-jost text-xs lg:text-base font-medium leading-4 lg:leading-5.5">
                  {remainingLabel} left
                </div>
              )}
              <div className="relative bg-gray-7 h-1.5 lg:h-2 rounded-full w-full overflow-hidden">
                <div
                  className={`absolute left-0 inset-y-0 h-full rounded-full ${progressPercentage >= 50 ? "bg-progress-subscription" : "bg-green-6"}`}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              {cancellationText && (
                <div className="text-gray-5 font-jost text-[11px] lg:text-sm font-normal leading-3.5 lg:leading-5">
                  {cancellationText}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col lg:flex-row lg:flex-wrap items-center gap-2 lg:gap-6 w-full">
            {showRenew && (
              <button
                onClick={() => setIsRenewSubscriptionModalOpen(true)}
                title="Renew subscription"
                className="theme-btn theme-btn-primary theme-btn-xs lg:theme-btn-md uppercase w-full max-w-[245px] lg:w-auto lg:max-w-none lg:min-w-[243px]">
                Renew subscription
                <ChevronsRightIcon variant={2} className="size-4 shrink-0 text-white" />
              </button>
            )}
            <button
              onClick={() => setIsBenefitsModalOpen(true)}
              title="View benefits"
              className="theme-btn theme-btn-primary-outline text-primary theme-btn-xs lg:theme-btn-md uppercase w-full max-w-[245px] lg:w-auto lg:max-w-none lg:min-w-[170px]">
              View benefits
              <ChevronsRightIcon variant={2} className="size-4 shrink-0 text-primary" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
